import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { useMemo } from 'react'

import { calculateDesiredSteps } from 'partner360/components/QBR/shared/QBRBarChart/helpers/calculateDesiredSteps'
import { getRoundBarYValue } from 'partner360/components/QBR/shared/QBRBarChart/helpers/getRoundBarYValue'
import { getFormattedFinance } from 'partner360/helpers/getFormattedFinance'

import { IQBRBarChartProps } from './models'

import styles from './QBRBarChart.module.scss'

export const QBRBarChart = ({
  chartName,
  chartLabelNode,
  barsSettings,
  linesSettings,
  height,
  selectedPeriodFYear,
  selectedPeriodFQuarters,
  isSob,
}: IQBRBarChartProps) => {
  const options = useMemo(() => {
    const allBarsData = barsSettings.map((el) => el.data).flat()

    const allLinesData = linesSettings.map((el) => el.data).flat()

    const maxValueForYAxis = isSob ? 100 : Math.max(...allBarsData)
    const minValueForYAxis = isSob ? 0 : Math.min(...allBarsData)

    const minValueForTrendLineYAxis = Math.min(...allLinesData)
    const maxValueForTrendLineYAxis = Math.max(...allLinesData)

    const { interval, steps } = calculateDesiredSteps(minValueForYAxis, maxValueForYAxis)

    const intervalForTrendLines = calculateDesiredSteps(
      minValueForTrendLineYAxis,
      maxValueForTrendLineYAxis,
      steps,
      steps
    )

    const yAxis = [
      {
        type: 'value',
        min: 0,
        max: maxValueForYAxis,
        interval: interval,
        axisLabel: {
          formatter: function (value: number) {
            return getFormattedFinance(getRoundBarYValue(value), { maximumFractionDigits: 0 })
          },
        },
      },
      {
        type: 'value',
        min: minValueForTrendLineYAxis,
        max: maxValueForTrendLineYAxis,
        interval: intervalForTrendLines,
        axisLabel: {
          formatter: '',
        },
      },
    ]

    const barsSeries = barsSettings.map((el) => ({
      name: el.name,
      type: 'bar',
      tooltip: {
        valueFormatter: function (value: number) {
          if (isFinite(value)) {
            return getFormattedFinance(value)
          }
          return '-'
        },
      },
      itemStyle: {
        color: el.color,
      },
      data: el.data.map((el) => (!isFinite(el) ? Infinity : el)),
    }))

    const linesSeries = linesSettings.map((el) => ({
      name: el.name,
      type: 'line',
      yAxisIndex: 1,
      label: {
        show: true,
        position: 'top',
        formatter: '{c}%',
      },
      itemStyle: {
        color: el.color,
      },
      tooltip: {
        valueFormatter: function (value: any) {
          if (Number.isInteger(value)) {
            return value + '%'
          }
          return '-'
        },
      },
      data: el.data.map((el) => (el === 0 ? null : el)),
    }))

    const series = [...barsSeries, ...linesSeries]
    const previousFYear = selectedPeriodFYear - 1
    const previousFYearLastTwoDigits = previousFYear.toString().slice(-2)
    const prevYearQuarters = Array.from({ length: 4 }, (_, i) => {
      const quarter = i + 1
      return `Q${quarter}'${previousFYearLastTwoDigits}`
    })
    const selectedFYearLastTwoDigits = selectedPeriodFYear.toString().slice(-2)
    const selectedYearQuarters = selectedPeriodFQuarters.map(
      (quarter) => `Q${quarter}'${selectedFYearLastTwoDigits}`
    )

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      legend: {
        data: [...barsSettings.map((el) => el.name), ...linesSettings.map((el) => el.name)],
      },
      xAxis: [
        {
          type: 'category',
          data: [...prevYearQuarters, ...selectedYearQuarters],
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis,
      series,
    } as EChartsOption
  }, [barsSettings, linesSettings, selectedPeriodFYear, selectedPeriodFQuarters])

  return (
    <div className={styles.root}>
      <RenderWhen condition={Boolean(chartName)}>
        <p className={styles.chartTitle}>{chartName}</p>
      </RenderWhen>

      <div className={styles.chartWrapper}>
        <RenderWhen condition={Boolean(chartLabelNode)}>
          <div className={styles.currencySettings}>{chartLabelNode}</div>
        </RenderWhen>

        <ReactEcharts option={options} opts={{ renderer: 'svg', height }} />
      </div>
    </div>
  )
}
